import { useState, useEffect } from "react";
import { Navigate, Link } from "react-router-dom";
import api, { imgUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { BarChart3, Users, Eye, CalendarDays, Radio, Globe, MapPin } from "lucide-react";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";

export default function Analytics() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user && user.role === "admin") {
      const fetchData = () => api.get("/admin/analytics").then(({ data }) => setData(data)).finally(() => setLoading(false));
      fetchData();
      const iv = setInterval(fetchData, 20000);
      return () => clearInterval(iv);
    } else if (user === false || (user && user.role !== "admin")) {
      setLoading(false);
    }
  }, [user]);

  if (user === false) return <Navigate to="/auth" replace />;
  if (user && user.role !== "admin") return <Navigate to="/" replace />;
  if (loading || !data) return <div className="mx-auto max-w-6xl px-4 py-16 text-center text-zinc-500">Bezig met laden...</div>;

  const cards = [
    { label: "Nu online", value: data.online_now, icon: Radio, testid: "stat-online-now", live: true },
    { label: "Bezoekers vandaag", value: data.unique_today, icon: Users, testid: "stat-unique-today" },
    { label: "Bezoeken vandaag", value: data.visits_today, icon: Eye, testid: "stat-visits-today" },
    { label: "Bezoeken deze week", value: data.visits_week, icon: CalendarDays, testid: "stat-visits-week" },
  ];

  const chartData = data.daily.map((d) => ({ ...d, label: d.date.slice(5) }));

  return (
    <div className="mx-auto max-w-6xl px-4 sm:px-6 py-10" data-testid="analytics-page">
      <div className="flex items-center gap-2 mb-2">
        <BarChart3 className="h-4 w-4 text-rose-400" />
        <span className="font-mono text-xs uppercase tracking-widest text-rose-400">Statistieken</span>
      </div>
      <h1 className="font-serif text-4xl font-semibold text-zinc-100 mb-8">Bezoekers</h1>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {cards.map((c) => (
          <div key={c.label} data-testid={c.testid} className="rounded-xl border border-zinc-800 bg-zinc-900/40 p-5 relative">
            <c.icon className={`h-5 w-5 mb-3 ${c.live ? "text-emerald-400" : "text-rose-400"}`} />
            {c.live && c.value > 0 && (
              <span className="absolute top-5 right-5 flex h-2.5 w-2.5">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500"></span>
              </span>
            )}
            <div className="font-mono text-3xl font-semibold text-zinc-100">{c.value}</div>
            <div className="text-xs text-zinc-500 mt-1">{c.label}</div>
          </div>
        ))}
      </div>

      <div className="rounded-xl border border-zinc-800 bg-zinc-900/40 p-5 mb-8">
        <h2 className="text-sm font-medium text-zinc-300 mb-4">Bezoeken laatste 30 dagen</h2>
        <div className="h-64" data-testid="visits-chart">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={chartData} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
              <defs>
                <linearGradient id="gv" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#f43f5e" stopOpacity={0.5} />
                  <stop offset="100%" stopColor="#f43f5e" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#27272a" vertical={false} />
              <XAxis dataKey="label" tick={{ fill: "#71717a", fontSize: 11 }} interval={4} axisLine={false} tickLine={false} />
              <YAxis tick={{ fill: "#71717a", fontSize: 11 }} axisLine={false} tickLine={false} allowDecimals={false} />
              <Tooltip contentStyle={{ background: "#18181b", border: "1px solid #3f3f46", borderRadius: 8, color: "#fff" }} labelStyle={{ color: "#a1a1aa" }} />
              <Area type="monotone" dataKey="visits" name="Bezoeken" stroke="#f43f5e" strokeWidth={2} fill="url(#gv)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <div className="rounded-xl border border-zinc-800 bg-zinc-900/40 p-5">
          <h2 className="text-sm font-medium text-zinc-300 mb-4 flex items-center gap-2"><Globe className="h-4 w-4 text-rose-400" /> Herkomst — landen</h2>
          {(!data.top_countries || data.top_countries.length === 0) ? (
            <p className="text-sm text-zinc-600">Nog geen data.</p>
          ) : (
            <ul className="space-y-2" data-testid="top-countries">
              {data.top_countries.map((c) => (
                <li key={c.country} className="flex items-center justify-between text-sm">
                  <span className="text-zinc-300 truncate">{c.country}</span>
                  <span className="text-rose-400 font-mono">{c.count}</span>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="rounded-xl border border-zinc-800 bg-zinc-900/40 p-5">
          <h2 className="text-sm font-medium text-zinc-300 mb-4 flex items-center gap-2"><MapPin className="h-4 w-4 text-rose-400" /> Herkomst — steden</h2>
          {(!data.top_cities || data.top_cities.length === 0) ? (
            <p className="text-sm text-zinc-600">Nog geen data.</p>
          ) : (
            <ul className="space-y-2" data-testid="top-cities">
              {data.top_cities.map((c) => (
                <li key={`${c.city}-${c.country}`} className="flex items-center justify-between text-sm">
                  <span className="text-zinc-300 truncate">{c.city}{c.country ? `, ${c.country}` : ""}</span>
                  <span className="text-rose-400 font-mono">{c.count}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-6 mt-6">
        <div className="rounded-xl border border-zinc-800 bg-zinc-900/40 p-5">
          <h2 className="text-sm font-medium text-zinc-300 mb-4">Drukste pagina's</h2>
          {data.top_paths.length === 0 ? (
            <p className="text-sm text-zinc-600">Nog geen data.</p>
          ) : (
            <ul className="space-y-2" data-testid="top-paths">
              {data.top_paths.map((p) => (
                <li key={p.path} className="flex items-center justify-between text-sm">
                  <span className="text-zinc-300 truncate font-mono text-xs">{p.path}</span>
                  <span className="text-rose-400 font-mono">{p.count}</span>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="rounded-xl border border-zinc-800 bg-zinc-900/40 p-5">
          <h2 className="text-sm font-medium text-zinc-300 mb-4">Meest bekeken advertenties</h2>
          {data.top_products.length === 0 ? (
            <p className="text-sm text-zinc-600">Nog geen data.</p>
          ) : (
            <ul className="space-y-3" data-testid="top-products">
              {data.top_products.map((p) => (
                <li key={p.id} className="flex items-center gap-3">
                  <Link to={`/product/${p.id}`} className="h-10 w-10 shrink-0 overflow-hidden rounded-lg bg-zinc-800">
                    {p.image ? <img src={imgUrl(p.image)} alt="" className="h-full w-full object-cover" /> : null}
                  </Link>
                  <Link to={`/product/${p.id}`} className="flex-1 min-w-0 text-sm text-zinc-300 truncate hover:text-rose-300">{p.title}</Link>
                  <span className="flex items-center gap-1 text-xs text-zinc-500"><Eye className="h-3 w-3" /> {p.views}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
